// Lays out numbered markers over a screenshot so that no number tab covers
// another box, another tab, or falls off the image. Works in rendered pixels,
// so it re-runs for any screenshot at any size.

export interface MarkerBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Corners first; the side-middle spots help in tight lists where every corner is busy.
export type TabCorner =
  | "top-right"
  | "top-left"
  | "bottom-right"
  | "bottom-left"
  | "right"
  | "left";

export type TabPlacement =
  | { kind: "attached"; corner: TabCorner; tab: MarkerBox }
  | { kind: "detached"; tab: MarkerBox; lineFrom: { x: number; y: number } };

export interface PlacedMarker {
  box: MarkerBox;
  placement: TabPlacement;
  // How much screenshot content the tab covers (see BusynessFn); exposed so QC can spot bad placements.
  coverage: number;
  tabHeight: number;
}

// Must match the outline's drawn corner radius and border in annotated-screenshot.tsx.
export const OUTLINE_RADIUS = 5;
const OUTLINE_BORDER = 2;
// Outline sits this far outside the element so it never covers what it points at.
const OUTLINE_GAP = 6;
// Tabs shrink with the screenshot so on a phone they stay in proportion to its text:
// 15px on a ~720px-wide desktop article, 11px on a ~340px phone.
export function tabHeightFor(imageWidth: number): number {
  return Math.min(15, Math.max(11, Math.round(imageWidth / 48)));
}
const DETACHED_DISTANCES = [10, 22, 36, 54, 76];
// Breathing room kept around other boxes and tabs when testing for overlap.
const CLEARANCE = 2;

function intersectionArea(a: MarkerBox, b: MarkerBox): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

function inflate(box: MarkerBox, by: number): MarkerBox {
  return { x: box.x - by, y: box.y - by, w: box.w + by * 2, h: box.h + by * 2 };
}

function outsideArea(box: MarkerBox, width: number, height: number): number {
  return box.w * box.h - intersectionArea(box, { x: 0, y: 0, w: width, h: height });
}

// The screenshot frame has 12px rounded corners that clip whatever sits in them.
// 3px is the smallest inset at which an outline's own 5px-rounded corner clears that curve.
const EDGE_INSET = 3;

function clampBox(box: MarkerBox, width: number, height: number): MarkerBox {
  const x = Math.max(EDGE_INSET, box.x);
  const y = Math.max(EDGE_INSET, box.y);
  return {
    x,
    y,
    w: Math.min(box.x + box.w, width - EDGE_INSET) - x,
    h: Math.min(box.y + box.h, height - EDGE_INSET) - y,
  };
}

const MIN_SEPARATION = 3;

// Pads by OUTLINE_GAP, unless a nearby target leaves less room on some side: then
// every side gets that smaller padding (at most half the space between them, so
// neighbouring outlines never touch), keeping the outline even instead of lopsided.
function padTowardNeighbours(target: MarkerBox, others: MarkerBox[]): MarkerBox {
  const overlapsX = (o: MarkerBox) => o.x < target.x + target.w && o.x + o.w > target.x;
  const overlapsY = (o: MarkerBox) => o.y < target.y + target.h && o.y + o.h > target.y;
  const room = (distances: number[]) =>
    Math.max(
      0,
      Math.min(
        OUTLINE_GAP,
        ...distances.filter((d) => d >= 0).map((d) => (d - MIN_SEPARATION) / 2),
      ),
    );
  const top = room(others.filter(overlapsX).map((o) => target.y - (o.y + o.h)));
  const bottom = room(others.filter(overlapsX).map((o) => o.y - (target.y + target.h)));
  const left = room(others.filter(overlapsY).map((o) => target.x - (o.x + o.w)));
  const right = room(others.filter(overlapsY).map((o) => o.x - (target.x + target.w)));
  return inflate(target, Math.min(top, bottom, left, right));
}

function attachedTab(
  box: MarkerBox,
  corner: TabCorner,
  tabWidth: number,
  tabHeight: number,
): MarkerBox {
  if (corner === "right" || corner === "left") {
    const x = corner === "right" ? box.x + box.w : box.x - tabWidth;
    return { x, y: box.y + box.h / 2 - tabHeight / 2, w: tabWidth, h: tabHeight };
  }
  const x = corner.endsWith("right") ? box.x + box.w - tabWidth : box.x;
  const y = corner.startsWith("top") ? box.y - tabHeight : box.y + box.h;
  return { x, y, w: tabWidth, h: tabHeight };
}

// Where a leader line meets the outline. Near a corner the drawn outline curves
// inward, so the point is moved onto that curve — otherwise the line stops short of it.
function nearestPointOnOutline(box: MarkerBox, point: { x: number; y: number }) {
  const nearest = {
    x: Math.min(Math.max(point.x, box.x), box.x + box.w),
    y: Math.min(Math.max(point.y, box.y), box.y + box.h),
  };
  const r = Math.min(OUTLINE_RADIUS, box.w / 2, box.h / 2);
  const centre = {
    x: Math.min(Math.max(nearest.x, box.x + r), box.x + box.w - r),
    y: Math.min(Math.max(nearest.y, box.y + r), box.y + box.h - r),
  };
  const inCorner = centre.x !== nearest.x && centre.y !== nearest.y;
  if (!inCorner) return nearest;
  const dx = point.x - centre.x;
  const dy = point.y - centre.y;
  const length = Math.hypot(dx, dy);
  return { x: centre.x + (dx / length) * r, y: centre.y + (dy / length) * r };
}

function detachedTabs(box: MarkerBox, tabWidth: number, tabHeight: number): MarkerBox[] {
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  return DETACHED_DISTANCES.flatMap((d) => [
    { x: box.x + box.w + d, y: box.y - tabHeight - d / 2 },
    { x: box.x - tabWidth - d, y: box.y - tabHeight - d / 2 },
    { x: box.x + box.w + d, y: cy - tabHeight / 2 },
    { x: box.x - tabWidth - d, y: cy - tabHeight / 2 },
    { x: cx - tabWidth / 2, y: box.y - tabHeight - d },
    { x: cx - tabWidth / 2, y: box.y + box.h + d },
    { x: box.x + box.w + d, y: box.y + box.h + d / 2 },
    { x: box.x - tabWidth - d, y: box.y + box.h + d / 2 },
  ]).map(({ x, y }) => ({ x, y, w: tabWidth, h: tabHeight }));
}

// A side tab on a box barely taller than itself would butt into the box's rounded
// corners, so it spans that whole side instead and the corners there are squared.
export function isShortBoxSideTab(
  placement: TabPlacement,
  box: MarkerBox,
  tabHeight: number,
): boolean {
  if (placement.kind !== "attached") return false;
  if (placement.corner !== "right" && placement.corner !== "left") return false;
  return box.h <= tabHeight + OUTLINE_RADIUS * 2 + OUTLINE_BORDER * 2;
}

// 0 = flat background; ~0.2 and up = text or icons under the tab. Lets tabs
// avoid covering screenshot content.
export type BusynessFn = (area: MarkerBox) => number;

// Sits between a panel border (~0.06, fine to cover) and text (~0.2): an
// attached tab that would cover text loses to a detached one on clear space.
const DETACH_PENALTY = 0.12;
const BLOCKED = 1000;

export function imageBusyness(pixels: ImageData): BusynessFn {
  const { data, width, height } = pixels;
  const luminance = (x: number, y: number) => {
    const i = (Math.round(y) * width + Math.round(x)) * 4;
    return data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
  };
  // Counts sharp brightness steps between neighbouring pixels. Text flips
  // between ink and background many times in a small area (~0.2), while a panel
  // border or a change of background flips once per row (~0.06).
  return (area) => {
    const x0 = Math.max(0, Math.round(area.x));
    const y0 = Math.max(0, Math.round(area.y));
    const x1 = Math.min(width - 1, Math.round(area.x + area.w));
    const y1 = Math.min(height - 1, Math.round(area.y + area.h));
    if (x1 <= x0 || y1 <= y0) return 1;
    let flips = 0;
    for (let y = y0; y <= y1; y += 1) {
      for (let x = x0 + 1; x <= x1; x += 1) {
        if (Math.abs(luminance(x, y) - luminance(x - 1, y)) > 16) flips += 1;
      }
    }
    for (let x = x0; x <= x1; x += 1) {
      for (let y = y0 + 1; y <= y1; y += 1) {
        if (Math.abs(luminance(x, y) - luminance(x, y - 1)) > 16) flips += 1;
      }
    }
    return flips / ((x1 - x0 + 1) * (y1 - y0 + 1));
  };
}

// Every box's outline is fixed first; tabs are then placed one by one. Each
// takes the cheapest candidate: never off-image or on another box/tab, then
// whichever covers the least screenshot content, preferring corners attached
// to the box. A detached tab gets a leader line back to its box.
export function layoutMarkers(
  percentBoxes: MarkerBox[],
  width: number,
  height: number,
  busyness: BusynessFn = () => 0,
): PlacedMarker[] {
  const targets = percentBoxes.map((b) => ({
    x: (b.x / 100) * width,
    y: (b.y / 100) * height,
    w: (b.w / 100) * width,
    h: (b.h / 100) * height,
  }));
  const boxes = targets.map((target, index) =>
    clampBox(
      padTowardNeighbours(
        target,
        targets.filter((_, i) => i !== index),
      ),
      width,
      height,
    ),
  );
  const placedTabs: MarkerBox[] = [];
  const tabHeight = tabHeightFor(width);

  return boxes.map((box, index) => {
    const tabWidth = index + 1 >= 10 ? tabHeight + 5 : tabHeight;
    const obstacles = [
      ...boxes.filter((_, i) => i !== index).map((b) => inflate(b, CLEARANCE)),
      ...placedTabs.map((t) => inflate(t, CLEARANCE)),
    ];
    const cost = (placement: TabPlacement) => {
      const { tab } = placement;
      const collisions =
        outsideArea(tab, width, height) +
        obstacles.reduce((sum, o) => sum + intersectionArea(tab, o), 0);
      if (collisions > 0) return BLOCKED + collisions;
      return busyness(tab) + (placement.kind === "detached" ? DETACH_PENALTY : 0);
    };

    const corners: TabCorner[] = [
      "top-right",
      "top-left",
      "bottom-right",
      "bottom-left",
      "right",
      "left",
    ];
    const candidates: TabPlacement[] = [
      ...corners.map(
        (corner): TabPlacement => ({
          kind: "attached",
          corner,
          tab: attachedTab(box, corner, tabWidth, tabHeight),
        }),
      ),
      ...detachedTabs(box, tabWidth, tabHeight).map(
        (tab): TabPlacement => ({
          kind: "detached",
          tab,
          lineFrom: nearestPointOnOutline(box, { x: tab.x + tab.w / 2, y: tab.y + tab.h / 2 }),
        }),
      ),
    ];

    // Ties keep the earlier candidate, so attached corners and short leader lines win.
    const best = candidates.reduce((a, b) => (cost(b) < cost(a) ? b : a));
    placedTabs.push(best.tab);
    const coverage = busyness(best.tab);
    if (!isShortBoxSideTab(best, box, tabHeight)) {
      return { box, placement: best, coverage, tabHeight };
    }
    const h = Math.max(box.h, tabHeight);
    const y = box.y + box.h / 2 - h / 2;
    return {
      box: { ...box, y, h },
      placement: { ...best, tab: { ...best.tab, y, h } },
      coverage,
      tabHeight,
    };
  });
}
